import { FileSearch, ZoomIn, ZoomOut, Ruler, Activity, AlertTriangle, Layers, Crosshair, Box, Circle, Wrench, Diamond, Triangle, Pencil, Save, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { KV } from "./atoms";
import Step3DViewer from "./Step3DViewer";
import EditableBRepCard from "./EditableBRepCard";
import EditableDFMCard from "./EditableDFMCard";
import CollapsibleCard from "./CollapsibleCard";
import FeatureSummaryRibbon from "./FeatureSummaryRibbon";
import api, { formatApiError } from "../../lib/api";
import { useState, useMemo } from "react";

const AXIS_COLORS = {
  length: "#22D3EE",
  width: "#F59E0B",
  height: "#A78BFA",
  diameter: "#34D399",
};
const AXIS_LABELS = { length: "L", width: "W", height: "H", diameter: "Ø" };

// Feature kind → color + symbol mapping for the DFM overlay layer.
// Severity overrides color when 'warn' (amber) or 'alert' (red).
const FEATURE_THEME = {
  hole:        { color: "#38BDF8", icon: "○", label: "Foro" },
  thread:      { color: "#F472B6", icon: "⌬", label: "Filettatura" },
  groove:      { color: "#A3E635", icon: "▭", label: "Cava" },
  pocket:      { color: "#FACC15", icon: "▣", label: "Tasca" },
  chamfer:     { color: "#94A3B8", icon: "◣", label: "Smusso" },
  fillet:      { color: "#94A3B8", icon: "◜", label: "Raccordo" },
  slot:        { color: "#22D3EE", icon: "▭", label: "Asola" },
  counterbore: { color: "#C084FC", icon: "⌽", label: "Lamatura" },
  countersink: { color: "#C084FC", icon: "▽", label: "Svasatura" },
};
const SEVERITY_COLORS = { warn: "#F59E0B", alert: "#EF4444" };

function AnnotationOverlay({ annotations, onHover, hovered }) {
  if (!annotations || annotations.length === 0) return null;
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="dim-overlay">
      {annotations.map((a, i) => {
        const bb = a.bbox_normalized;
        if (!Array.isArray(bb) || bb.length !== 4) return null;
        const [x1, y1, x2, y2] = bb;
        const color = AXIS_COLORS[a.axis] || "#22D3EE";
        const label = `${AXIS_LABELS[a.axis] || "?"} = ${a.value_mm} mm`;
        const isHovered = hovered === `dim-${i}`;
        return (
          <div
            key={`ann-${i}`}
            className="absolute pointer-events-auto transition-all duration-200"
            style={{
              left: `${x1 * 100}%`,
              top: `${y1 * 100}%`,
              width: `${(x2 - x1) * 100}%`,
              height: `${(y2 - y1) * 100}%`,
              border: `2px solid ${color}`,
              borderRadius: 2,
              backgroundColor: isHovered ? `${color}22` : `${color}10`,
              boxShadow: isHovered ? `0 0 12px ${color}` : "none",
            }}
            onMouseEnter={() => onHover(`dim-${i}`)}
            onMouseLeave={() => onHover(null)}
            data-testid={`annotation-${a.axis}`}
          >
            <span
              className="absolute -top-7 left-0 px-2 py-0.5 text-[11px] font-mono-data font-bold rounded-sm shadow"
              style={{ backgroundColor: color, color: "#0F172A" }}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function FeatureOverlay({ features, onHover, hovered, kindFilter, theme = FEATURE_THEME }) {
  if (!features || features.length === 0) return null;
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="feature-overlay">
      {features.map((f, i) => {
        const bb = f.bbox_normalized;
        if (!Array.isArray(bb) || bb.length !== 4) return null;
        const [x1, y1, x2, y2] = bb;
        const th = theme[f.kind] || theme.hole;
        const severity = (f.severity || "info").toLowerCase();
        const color = SEVERITY_COLORS[severity] || th.color;
        const isHovered = hovered === `feat-${i}`;
        const dimmed = kindFilter && kindFilter !== f.kind;
        const spotlighted = kindFilter && kindFilter === f.kind;
        const label = f.label || th.label;
        return (
          <div
            key={`feat-${i}`}
            className={`absolute pointer-events-auto transition-all duration-200 ${spotlighted ? "feature-spotlight" : ""}`}
            style={{
              left: `${x1 * 100}%`,
              top: `${y1 * 100}%`,
              width: `${(x2 - x1) * 100}%`,
              height: `${(y2 - y1) * 100}%`,
              border: `2px dashed ${color}`,
              borderRadius: 6,
              backgroundColor: isHovered ? `${color}33` : `${color}14`,
              boxShadow: isHovered || spotlighted ? `0 0 14px ${color}` : "none",
              opacity: dimmed ? 0.18 : 1,
            }}
            onMouseEnter={() => onHover(`feat-${i}`)}
            onMouseLeave={() => onHover(null)}
            data-testid={`feature-${f.kind}`}
            title={`${th.label} — ${label}${f.quantity > 1 ? ` ×${f.quantity}` : ""}`}
          >
            <span
              className="absolute -top-7 left-0 px-2 py-0.5 text-[11px] font-mono-data font-bold rounded shadow flex items-center gap-1 whitespace-nowrap"
              style={{ backgroundColor: color, color: "#0F172A" }}
            >
              <span aria-hidden="true">{th.icon}</span>
              {label}
              {f.quantity > 1 && <span className="opacity-80">×{f.quantity}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function ViewerTab({ q, a, c, dims, imageBlobUrl, pdfBlobUrl, zoom, setZoom, onQuoteUpdate }) {
  const { t } = useTranslation();
  const hasImage = imageBlobUrl || q.image_base64;
  const hasPdf = !!pdfBlobUrl;
  const has3D = !!q.cad3d_path;
  const annotations = a?.dimension_annotations || [];
  const featureAnnotations = a?.feature_annotations || [];
  const [showAnn, setShowAnn] = useState(true);
  const [showFeat, setShowFeat] = useState(true);
  const [kindFilter, setKindFilter] = useState("");
  const [hovered, setHovered] = useState(null);
  const [mode, setMode] = useState(has3D ? "3d" : "2d");
  const [savingDim, setSavingDim] = useState(false);
  const [dimDraft, setDimDraft] = useState(null);

  const featureTheme = useMemo(() => {
    const out = {};
    Object.entries(FEATURE_THEME).forEach(([k, v]) => { out[k] = { ...v, label: t(`viewerTab.feat.${k}`) }; });
    return out;
  }, [t]);

  // Feature data from backend OpenCascade analyzer (when STEP/IGES was uploaded)
  const features = q?.source_meta?.features;
  const candidateHoles = (features?.candidate_holes) || [];
  const partBBox = features?.bbox_mm || null;

  // Patch helper used by the editable cards. Calls /update-analysis and
  // bubbles the refreshed quote up to QuoteDetailPage via onQuoteUpdate.
  const saveAnalysisPatch = async (patch) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/update-analysis`, patch);
      onQuoteUpdate?.(data);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(formatApiError(err.response?.data?.detail) || err.message || "Errore");
      throw err;
    }
  };

  const startDimEdit = () => setDimDraft({
    length_mm: dims?.length_mm ?? "",
    width_mm: dims?.width_mm ?? "",
    height_mm: dims?.height_mm ?? "",
    diameter_mm: dims?.diameter_mm ?? "",
  });
  const cancelDimEdit = () => setDimDraft(null);
  const saveDimEdit = async () => {
    setSavingDim(true);
    const clean = {};
    Object.entries(dimDraft).forEach(([k, v]) => {
      const num = parseFloat(v);
      clean[k] = isNaN(num) ? null : num;
    });
    try {
      await saveAnalysisPatch({ estimated_dimensions_mm: clean });
      setDimDraft(null);
    } finally {
      setSavingDim(false);
    }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5" data-testid="viewer-tab">
      <div className="space-y-4">
        {has3D && (
          <div className="flex items-center gap-2" data-testid="viewer-mode-switcher">
            <button
              onClick={() => setMode("3d")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider border transition-colors ${mode === "3d" ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}
              data-testid="mode-3d"
            >
              <Box size={12}/> {t("viewerTab.mode3d")}
            </button>
            <button
              onClick={() => setMode("2d")}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider border transition-colors ${mode === "2d" ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}
              data-testid="mode-2d"
            >
              <FileSearch size={12}/> {t("viewerTab.mode2d")}
            </button>
          </div>
        )}

        {has3D && mode === "3d" ? (
          <>
            {showFeat && featureAnnotations.length > 0 && (
              <FeatureSummaryRibbon
                features={featureAnnotations}
                theme={featureTheme}
                kindFilter={kindFilter}
                setKindFilter={setKindFilter}
              />
            )}
            <Step3DViewer
              quoteId={q.id}
              holes={candidateHoles}
              partBBox={partBBox}
              height={560}
              hovered={hovered}
            />
          </>
        ) : (
          <>
            {showFeat && featureAnnotations.length > 0 && (
              <FeatureSummaryRibbon
                features={featureAnnotations}
                theme={featureTheme}
                kindFilter={kindFilter}
                setKindFilter={setKindFilter}
              />
            )}
            <div className="tech-card overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-700/60 flex items-center justify-between">
              <div className="label-tech flex items-center gap-2"><FileSearch size={12} className="text-blue-400"/> {hasPdf && !hasImage ? "Time Study PDF" : t("viewerTab.drawingPhoto")}</div>
              <div className="flex items-center gap-2">
                {hasImage && annotations.length > 0 && (
                  <button
                    onClick={() => setShowAnn((v) => !v)}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] border transition-colors ${showAnn ? "border-cyan-500/60 text-cyan-300 bg-cyan-500/10" : "border-slate-700 text-slate-500 hover:border-slate-500"}`}
                    title={t("viewerTab.toggleAnnTitle")}
                    data-testid="toggle-annotations"
                  >
                    <Crosshair size={11}/> {t("viewerTab.quotesAi")} ({annotations.length})
                  </button>
                )}
                {hasImage && featureAnnotations.length > 0 && (
                  <button
                    onClick={() => setShowFeat((v) => !v)}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] border transition-colors ${showFeat ? "border-pink-500/60 text-pink-300 bg-pink-500/10" : "border-slate-700 text-slate-500 hover:border-slate-500"}`}
                    title={t("viewerTab.toggleFeatTitle")}
                    data-testid="toggle-features"
                  >
                    <Diamond size={11}/> DFM ({featureAnnotations.length})
                  </button>
                )}
                {hasImage && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="p-1.5 rounded hover:bg-slate-800 text-slate-400" data-testid="zoom-out"><ZoomOut size={14}/></button>
                    <span className="font-mono-data text-xs text-slate-400 w-12 text-center">{Math.round(zoom * 100)}%</span>
                    <button onClick={() => setZoom((z) => Math.min(3, z + 0.25))} className="p-1.5 rounded hover:bg-slate-800 text-slate-400" data-testid="zoom-in"><ZoomIn size={14}/></button>
                  </div>
                )}
                {hasPdf && !hasImage && (
                  <a href={pdfBlobUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid="pdf-open-new">
                    {t("viewerTab.openNewTab")}
                  </a>
                )}
              </div>
            </div>
            <div className="bg-slate-950 min-h-[420px] max-h-[680px] overflow-auto flex items-center justify-center">
              {hasImage ? (
                <div className="relative inline-block" style={{ transform: `scale(${zoom})`, transformOrigin: "center", transition: "transform .2s" }}>
                  <img
                    src={imageBlobUrl || `data:image/jpeg;base64,${q.image_base64}`}
                    alt="part"
                    className="max-w-full object-contain p-4 block"
                    data-testid="part-image"
                  />
                  {showAnn && <AnnotationOverlay annotations={annotations} onHover={setHovered} hovered={hovered}/>}
                  {showFeat && <FeatureOverlay features={featureAnnotations} onHover={setHovered} hovered={hovered} kindFilter={kindFilter} theme={featureTheme}/>}
                </div>
              ) : hasPdf ? (
                <iframe
                  src={pdfBlobUrl}
                  title={t("viewerTab.timeStudyPdf")}
                  className="w-full h-[680px] border-0"
                  data-testid="pdf-iframe"
                />
              ) : (
                <div className="text-slate-500 text-sm flex flex-col items-center gap-2 p-8">
                  <Layers size={32} className="text-slate-700"/>
                  <span>{t("viewerTab.noDocument")}</span>
                </div>
              )}
            </div>
          </div>
          </>
        )}
      </div>

      <div className="space-y-4">
        <EditableBRepCard
          features={features}
          onSave={saveAnalysisPatch}
          onHoverHole={setHovered}
          hovered={hovered}
        />

        <EditableDFMCard
          features={featureAnnotations}
          hovered={hovered}
          onHoverFeature={setHovered}
          onSave={saveAnalysisPatch}
        />

        <CollapsibleCard
          testid="dims-card"
          storageKey="dims"
          icon={<Ruler size={12} className="text-blue-400"/>}
          title={t("viewerTab.dimsTitle")}
          actions={!dimDraft ? (
            <button
              type="button"
              onClick={startDimEdit}
              className="p-1 rounded text-slate-400 hover:text-blue-300 hover:bg-blue-500/10"
              title={t("viewerTab.editDims")}
              data-testid="dims-edit-btn"
            >
              <Pencil size={12}/>
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <button type="button" onClick={cancelDimEdit} className="p-1 rounded text-slate-400 hover:text-rose-300" data-testid="dims-cancel-btn">
                <X size={12}/>
              </button>
              <button
                type="button"
                onClick={saveDimEdit}
                disabled={savingDim}
                className="p-1 rounded text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                data-testid="dims-save-btn"
              >
                <Save size={12}/>
              </button>
            </div>
          )}
        >
          {!dimDraft ? (
            <div className="space-y-2 text-sm">
              <KV k={t("editAnalysis.length")} v={`${dims.length_mm || "-"} mm`}/>
              <KV k={t("editAnalysis.width")} v={`${dims.width_mm || "-"} mm`}/>
              <KV k={t("editAnalysis.height")} v={`${dims.height_mm || "-"} mm`}/>
              {dims.diameter_mm && <KV k={t("editAnalysis.diameter")} v={`${dims.diameter_mm} mm`}/>}
              <KV k={t("viewerTab.rawVolume")} v={`${c.volume_cm3?.toFixed(1) || "-"} cm³`}/>
              <KV k={t("viewerTab.rawWeight")} v={`${c.material?.weight_kg?.toFixed(2) || "-"} kg`}/>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              {[["length_mm", t("editAnalysis.length")], ["width_mm", t("editAnalysis.width")], ["height_mm", t("editAnalysis.height")], ["diameter_mm", t("editAnalysis.diameter")]].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between gap-2">
                  <span className="text-slate-400 text-xs">{label}</span>
                  <input
                    type="number"
                    step="0.1"
                    value={dimDraft[key] ?? ""}
                    onChange={(e) => setDimDraft({ ...dimDraft, [key]: e.target.value })}
                    className="w-24 px-2 py-1 bg-slate-900 border border-slate-700 rounded text-xs text-right"
                    placeholder="mm"
                    data-testid={`dims-${key}-input`}
                  />
                </div>
              ))}
              <p className="text-[10px] text-slate-500 pt-2 border-t border-slate-800">
                {t("viewerTab.saveRecalcNote")}
              </p>
            </div>
          )}
        </CollapsibleCard>

        <CollapsibleCard
          testid="features-card"
          storageKey="features"
          icon={<Activity size={12} className="text-blue-400"/>}
          title={t("viewerTab.characteristics")}
        >
          <div className="space-y-2 text-sm">
            <KV k={t("viewerTab.geometry")} v={a.geometry_type || "-"}/>
            <KV k={t("viewerTab.complexity")} v={a.complexity || "-"}/>
            <KV k={t("editAnalysis.tolClass")} v={a.tolerance_class || "-"}/>
            <KV k={t("viewerTab.finishRa")} v={`${a.surface_finish_ra_um || "-"} µm`}/>
          </div>
        </CollapsibleCard>

        {a.warnings?.length > 0 && (
          <CollapsibleCard
            testid="warnings-card"
            storageKey="warnings"
            className="border-amber-500/40"
            titleClassName="text-amber-400"
            icon={<AlertTriangle size={14} className="text-amber-400"/>}
            title={t("viewerTab.aiWarnings")}
          >
            <ul className="space-y-2 text-sm text-amber-200/80">
              {a.warnings.map((w, i) => <li key={`warn-${i}`}>• {w}</li>)}
            </ul>
          </CollapsibleCard>
        )}

        {a.manual_features && Object.entries(a.manual_features).some(([k, v]) => k !== "updated_at" && v) && (
          <CollapsibleCard
            testid="manual-features-card"
            storageKey="manual"
            className="border-amber-500/40"
            titleClassName="text-amber-300"
            icon={<Activity size={14} className="text-amber-300"/>}
            title={t("viewerTab.manualData")}
          >
            <div className="space-y-2 text-sm">
              {a.manual_features.threads_external && <KV k={t("viewerTab.threadsExtPl")} v={a.manual_features.threads_external}/>}
              {a.manual_features.threads_internal && <KV k={t("viewerTab.threadsIntPl")} v={a.manual_features.threads_internal}/>}
              {a.manual_features.grooves && <KV k={t("viewerTab.groovesSeeger")} v={a.manual_features.grooves}/>}
              {a.manual_features.keyways && <KV k={t("editAnalysis.keyways")} v={a.manual_features.keyways}/>}
              {a.manual_features.knurling && <KV k={t("editAnalysis.knurling")} v={a.manual_features.knurling}/>}
              {a.manual_features.chamfers && <KV k={t("viewerTab.chamfersShort")} v={a.manual_features.chamfers}/>}
              {a.manual_features.other_features && (
                <div className="pt-2 border-t border-slate-800 text-slate-300 leading-relaxed">
                  <div className="label-tech text-[10px] mb-1">{t("viewerTab.otherData")}</div>
                  <div className="whitespace-pre-wrap">{a.manual_features.other_features}</div>
                </div>
              )}
            </div>
          </CollapsibleCard>
        )}
      </div>
    </div>
  );
}
